import { Injectable, EventEmitter, Output } from "@angular/core";

import { BehaviorSubject } from "rxjs/BehaviorSubject";
import { Observable } from "rxjs/Observable";
import "rxjs/add/operator/finally";

import { environment } from "../../../environments/environment";
import { DataService } from "./data.service";
import { OfferEntryDataService } from "./offer-entry-data.service";

import {
  ClearedOfferFileData,
  TableRow,
} from "../../shared/components/offer-summary-confirmation-dialog/clearedOfferFile";

import { GetData } from "../../cleared-offer-mock-data";
import { HttpHeaders } from "@angular/common/http";

import { SpinnerService } from "./spinner.service";
import { UtilsService } from "./utils.service";

@Injectable()
export class OfferSummaryDataService {
  private useMockData = false;

  private cData: ClearedOfferFileData;
  private offerSummaryData;
  private offerSummaryDataLoaded: boolean = false;
  private fileToPost: File;
  private portfolioId: string;
  private tradingDate: string;
  private product: string;

  constructor(
    private data: DataService,
    private offerEntryDataService: OfferEntryDataService,
    private spinnerService: SpinnerService,
    private utilsService: UtilsService
  ) {
    this.initBlankData();
    this.offerSummaryData = new BehaviorSubject<ClearedOfferFileData>(
      this.cData
    );
  }

  private initBlankData() {
    if (this.useMockData) {
      this.cData = GetData();
    } else {
      this.cData = {
        name: "",
        date_time: null,
        date_time_label: "",
        header: {
          resource: "",
          trading_date: "",
          hour: "",
          offered_volume: "",
          cleared_volume: "",
          percentage_cleared: "",
        },
        data: [],
        loaded: false,
        error: false,
        saved: false,
      };
    }
  }

  private getHeader(): HttpHeaders {
    let headers = new HttpHeaders();
    const programId = sessionStorage.getItem("currentProgram");
    this.utilsService.debugLog(programId);
    if (programId) {
      headers = headers.set("Program", programId);
    }

    return headers;
  }

  analyseFile(
    fileToUpload: File,
    portfolioId: string,
    tradingDate: string,
    product: string
  ) {
    this.fileToPost = fileToUpload;
    this.portfolioId = portfolioId;
    this.tradingDate = tradingDate;
    this.product = product;
    this.postFile(false);
  }

  postFile(writeToServer: boolean) {
    this.cData.loaded = false;
    this.cData.error = false;
    this.cData.saved = false;

    if (this.fileToPost && !this.useMockData) {
      const reader = new FileReader();

      reader.onload = (readerEvt) => {
        let write_to_db = "0";
        if (writeToServer) {
          write_to_db = "1";
        }

        const endpoint =
          environment.offer_summary_url +
          "?filename=" +
          encodeURIComponent(this.fileToPost.name) +
          "&write_to_db=" +
          write_to_db +
          "&portfolio_id=" +
          this.portfolioId +
          "&trading_date=" +
          this.tradingDate +
          "&product=" +
          this.product;
        const binaryString = (<FileReader>readerEvt.target).result;
        const base64 = btoa(binaryString);
        this.utilsService.debugLog("posting file");
        // this.utilsService.debugLog(base64);
        this.spinnerService.show("uploadSpinner");
        this.data
          .post(endpoint, base64, this.getHeader())
          .finally(() => {
            this.spinnerService.hide("uploadSpinner");
          })
          .map((response) => {
            if (response) {
              this.utilsService.debugLog("received response: ", response);
              this.cData = this.decodeofferSummaryDataResponse(
                response["data"]
              );
            }
          })
          .subscribe(
            (data) => {
              // do something, if upload success
              this.cData.loaded = true;
              this.cData.saved = writeToServer;
              this.offerSummaryData.next(this.cData);
              if (writeToServer) {
                this.offerEntryDataService.reloadGridData(); // load the grid since we've uploaded the file
              }
            },
            (error) => {
              this.cData.error = true;
              this.offerSummaryData.next(this.cData);
            }
          );
      };

      reader.readAsBinaryString(this.fileToPost);
    }

    this.offerSummaryData.next(this.cData);
  }

  getClearedOfferFileData(): Observable<ClearedOfferFileData> {
    return this.offerSummaryData.asObservable();
  }

  private decodeofferSummaryDataResponse(dataResponse): ClearedOfferFileData {
    const offerSummaryData: ClearedOfferFileData = {
      name: "",
      date_time: null,
      date_time_label: "",
      header: {
        resource: "",
        trading_date: "",
        hour: "",
        offered_volume: "",
        cleared_volume: "",
        percentage_cleared: "",
      },
      data: [],
      loaded: false,
      error: false,
      saved: true,
    };

    offerSummaryData.name = dataResponse.name;
    offerSummaryData.date_time = new Date(dataResponse.date_time);
    offerSummaryData.date_time_label = dataResponse.date_time_label;

    offerSummaryData.header.resource = dataResponse.table_header.resource;
    offerSummaryData.header.trading_date =
      dataResponse.table_header.trading_date;
    offerSummaryData.header.hour = dataResponse.table_header.hour;
    offerSummaryData.header.offered_volume =
      dataResponse.table_header.offered_volume;
    offerSummaryData.header.cleared_volume =
      dataResponse.table_header.cleared_volume;
    offerSummaryData.header.percentage_cleared =
      dataResponse.table_header.percentage_cleared;

    for (const row of dataResponse.table_data.rows) {
      const newRow: TableRow = {
        resource: row.resource,
        trading_date: row.trading_date,
        hour: row.hour,
        offered_volume: row.offered_volume,
        cleared_volume: row.cleared_volume,
        percentage_cleared: parseFloat(row.percentage_cleared).toFixed(0),
      };
      if (newRow.cleared_volume !== "0" || newRow.percentage_cleared !== "0") {
        // ignore 0 0 0 rows
        offerSummaryData.data.push(newRow);
      }
    }

    // this.utilsService.debugLog('decoded data: ');
    // this.utilsService.debugLog(offerSummaryData);
    return offerSummaryData;
  }
}



// WEBPACK FOOTER //
// ./src/app/core/services/offer-summary-data.service.ts